/**
 * LiteratureDiscoveryPanel Component
 * Provides literature discovery based on search queries
 */

import React, { useState } from 'react'
import { logError } from '@utils/logger'
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  BookOpenIcon,
  DocumentArrowUpIcon,
  FunnelIcon,
  BookmarkIcon,
  ArrowPathIcon,
  AcademicCapIcon,
  PlusIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { CheckIcon as CheckIconSolid } from '@heroicons/react/24/solid'
import toast from 'react-hot-toast'
import clsx from 'clsx'

import { researchLiteratureAPI, type LiteratureDiscoveryRequest } from '@/api/researchLiterature'
import { citationLibraryAPI } from '@/api/citationLibrary'
import type { CitationPaper } from '@/api/citations'
import { generateBibtexEntry, generateBibtexKey } from '@/utils/generateBibtex'
import { useQueryClient } from '@tanstack/react-query'
import PaperDetailModal from './PaperDetailModal'

interface LiteratureDiscoveryPanelProps {
  documentId?: string
  isOpen: boolean
  onClose: () => void
  onCitationInsert?: (citation: string, paper?: CitationPaper) => void
}

export const LiteratureDiscoveryPanel: React.FC<LiteratureDiscoveryPanelProps> = ({
  documentId,
  isOpen,
  onClose,
  onCitationInsert,
}) => {
  const queryClient = useQueryClient()
  const [query, setQuery] = useState<string>('')
  const [papers, setPapers] = useState<CitationPaper[]>([])
  const [isDiscovering, setIsDiscovering] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [selectedPapers, setSelectedPapers] = useState<Set<string>>(new Set())
  const [filters, setFilters] = useState({
    yearFrom: '',
    yearTo: '',
    maxResults: 50,
    customInstructions: '',
  })
  const [showFilters, setShowFilters] = useState(false)
  const [selectedPaper, setSelectedPaper] = useState<CitationPaper | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleDiscover = async () => {
    if (!query.trim()) {
      return
    }

    setIsDiscovering(true)
    setPapers([])
    setSelectedPapers(new Set())

    try {
      const request: LiteratureDiscoveryRequest = {
        query: query,
        max_results: filters.maxResults,
        year_from: filters.yearFrom ? parseInt(filters.yearFrom) : undefined,
        year_to: filters.yearTo ? parseInt(filters.yearTo) : undefined,
        custom_instructions: filters.customInstructions || undefined,
      }

      const response = await researchLiteratureAPI.discoverLiterature(request)
      setPapers(response.papers)
      
      // Invalidate token usage queries after successful literature discovery
      if (documentId) {
        queryClient.invalidateQueries({ queryKey: ['token-usage', 'document', documentId] })
      }
      queryClient.invalidateQueries({ queryKey: ['token-usage', 'user-stats'] })
    } catch (error: any) {
      logError('Error discovering literature:', error)
      const detail = error?.response?.data?.detail
      let message = 'Failed to discover literature'
      if (typeof detail === 'string') {
        message = detail
      } else if (detail && typeof detail === 'object') {
        message = detail.message || detail.error || message
      } else if (error?.message) {
        message = error.message
      }
      toast.error(message || 'Something went wrong. Please contact the administrator.')
    } finally {
      setIsDiscovering(false)
    }
  }

  const handlePDFUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.name.endsWith('.pdf')) {
      return
    }

    setIsUploading(true)

    try {
      const response = await researchLiteratureAPI.analyzePDF(file)

      if (response.paper) {
        // Add to papers list
        setPapers(prev => [response.paper, ...prev])
        
        // Optionally save to library
        if (documentId) {
          await handleSaveToLibrary([response.paper])
        }
      }
    } catch (error: any) {
      logError('Error analyzing PDF:', error)
      const detail = error?.response?.data?.detail
      let message = 'Failed to analyze PDF'
      if (typeof detail === 'string') {
        message = detail
      } else if (detail && typeof detail === 'object') {
        message = detail.message || detail.error || message
      } else if (error?.message) {
        message = error.message
      }
      toast.error(message || 'Something went wrong. Please contact the administrator.')
    } finally {
      setIsUploading(false)
      // Reset file input
      event.target.value = ''
    }
  }

  const togglePaperSelection = (paperTitle: string) => {
    setSelectedPapers(prev => {
      const next = new Set(prev)
      if (next.has(paperTitle)) {
        next.delete(paperTitle)
      } else {
        next.add(paperTitle)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    if (selectedPapers.size === papers.length) {
      setSelectedPapers(new Set())
    } else {
      setSelectedPapers(new Set(papers.map(p => p.title)))
    }
  }

  const handleSaveToLibrary = async (papersToSave?: CitationPaper[]) => {
    if (!documentId) {
      return
    }

    const papersToProcess = papersToSave || papers.filter(p => selectedPapers.has(p.title))

    if (papersToProcess.length === 0) {
      return
    }

    try {
      let savedCount = 0
      for (const paper of papersToProcess) {
        try {
          const bibtexKey = generateBibtexKey(paper)
          const bibtexEntry = generateBibtexEntry(paper, bibtexKey)

          await citationLibraryAPI.saveCitation(documentId, {
            title: paper.title,
            authors: paper.authors,
            year: paper.year,
            doi: paper.doi,
            pmid: paper.pmid,
            arxiv_id: paper.arxiv_id,
            abstract: paper.abstract,
            venue: paper.venue,
            url: paper.url,
            bibtex_key: bibtexKey,
            bibtex_entry: bibtexEntry,
          })
          savedCount++
        } catch (error) {
          logError(`Error saving paper "${paper.title}":`, error)
        }
      }

      setSelectedPapers(new Set())
    } catch (error: any) {
      logError('Error saving papers:', error)
    }
  }

  const handleInsertCitation = (paper: CitationPaper) => {
    if (!onCitationInsert) return

    const bibtexKey = generateBibtexKey(paper)
    const citationText = `\\cite{${bibtexKey}}`
    onCitationInsert(citationText, paper)
  }

  const handleSaveSinglePaper = async (paper: CitationPaper) => {
    if (!documentId) {
      return
    }

    try {
      const bibtexKey = generateBibtexKey(paper)
      const bibtexEntry = generateBibtexEntry(paper, bibtexKey)

      await citationLibraryAPI.saveCitation(documentId, {
        title: paper.title,
        authors: paper.authors,
        year: paper.year,
        doi: paper.doi,
        pmid: paper.pmid,
        arxiv_id: paper.arxiv_id,
        abstract: paper.abstract,
        venue: paper.venue,
        url: paper.url,
        bibtex_key: bibtexKey,
        bibtex_entry: bibtexEntry,
      })

    } catch (error: any) {
      logError('Error saving paper:', error)
    }
  }

  const handleDiscardPaper = (paperTitle: string) => {
    setPapers(prev => prev.filter(p => p.title !== paperTitle))
    setSelectedPapers(prev => {
      const next = new Set(prev)
      next.delete(paperTitle)
      return next
    })
  }

  const handlePaperClick = (paper: CitationPaper, e: React.MouseEvent) => {
    // Don't open modal if clicking on buttons or links
    const target = e.target as HTMLElement
    if (
      target.closest('button') ||
      target.closest('a') ||
      target.closest('[role="button"]')
    ) {
      return
    }
    setSelectedPaper(paper)
    setIsModalOpen(true)
  }

  if (!isOpen) return null

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <BookOpenIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Literature Discovery
          </h2>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Search Query Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Search Query or Research Description
          </label>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g., 'High-fidelity combustion simulation methods' or 'Explain quantum entanglement'"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isDiscovering}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={handleDiscover}
            disabled={!query.trim() || isDiscovering}
            className={clsx(
              'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
              !query.trim() || isDiscovering
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            )}
          >
            {isDiscovering ? (
              <>
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                Discovering...
              </>
            ) : (
              <>
                <MagnifyingGlassIcon className="w-4 h-4" />
                Discover Literature
              </>
            )}
          </button>

          <label className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors cursor-pointer">
            <DocumentArrowUpIcon className="w-4 h-4" />
            Upload PDF
            <input
              type="file"
              accept=".pdf"
              onChange={handlePDFUpload}
              className="hidden"
              disabled={isUploading}
            />
          </label>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={clsx(
              'p-2 rounded-lg transition-colors',
              showFilters
                ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400'
                : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
            )}
          >
            <FunnelIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Year From
                </label>
                <input
                  type="number"
                  value={filters.yearFrom}
                  onChange={(e) => setFilters({ ...filters, yearFrom: e.target.value })}
                  placeholder="e.g., 2020"
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Year To
                </label>
                <input
                  type="number"
                  value={filters.yearTo}
                  onChange={(e) => setFilters({ ...filters, yearTo: e.target.value })}
                  placeholder="e.g., 2024"
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Max Results
              </label>
              <input
                type="number"
                value={filters.maxResults}
                onChange={(e) => setFilters({ ...filters, maxResults: parseInt(e.target.value) || 50 })}
                min={1}
                max={100}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Custom Instructions (Optional)
              </label>
              <textarea
                value={filters.customInstructions}
                onChange={(e) => setFilters({ ...filters, customInstructions: e.target.value })}
                placeholder="e.g., Focus on recent deep learning papers, prioritize papers from top-tier conferences..."
                rows={3}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Add specific requirements or focus areas for the literature search
              </p>
            </div>
          </div>
        )}

        {/* Results */}
        {papers.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Found {papers.length} papers
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={handleSelectAll}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {selectedPapers.size === papers.length ? 'Deselect All' : 'Select All'}
                </button>
                {documentId && selectedPapers.size > 0 && (
                  <button
                    onClick={() => handleSaveToLibrary()}
                    className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 hover:underline"
                  >
                    <BookmarkIcon className="w-3 h-3" />
                    Save {selectedPapers.size} to Library
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-3">
              {papers.map((paper, index) => {
                const isSelected = selectedPapers.has(paper.title)
                return (
                  <div
                    key={index}
                    className={clsx(
                      'p-4 rounded-lg border transition-colors cursor-pointer',
                      isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                    )}
                    onClick={(e) => handlePaperClick(paper, e)}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          togglePaperSelection(paper.title)
                        }}
                        className="mt-1"
                      >
                        {isSelected ? (
                          <CheckIconSolid className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 rounded hover:border-blue-500 transition-colors" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-1 line-clamp-2">
                          {paper.title}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          {paper.authors.join(', ')}
                          {paper.year && ` • ${paper.year}`}
                          {paper.venue && ` • ${paper.venue}`}
                        </p>
                        {paper.abstract && (
                          <p className="text-xs text-gray-500 dark:text-gray-500 line-clamp-2 mb-2">
                            {paper.abstract}
                          </p>
                        )}
                        {paper.relevance_score && (
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <AcademicCapIcon className="w-3 h-3" />
                            Relevance: {(paper.relevance_score * 100).toFixed(0)}%
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {paper.doi && (
                            <a
                              href={`https://doi.org/${paper.doi}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              View Paper
                            </a>
                          )}
                          {onCitationInsert && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleInsertCitation(paper)
                              }}
                              className="text-xs text-green-600 dark:text-green-400 hover:underline"
                            >
                              Insert Citation
                            </button>
                          )}
                          {documentId && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleSaveSinglePaper(paper)
                              }}
                              className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 hover:underline"
                              title="Add to library"
                            >
                              <PlusIcon className="w-3 h-3" />
                              Add to Library
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDiscardPaper(paper.title)
                            }}
                            className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:underline"
                            title="Remove from results"
                          >
                            <XCircleIcon className="w-3 h-3" />
                            Discard
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isDiscovering && papers.length === 0 && (
          <div className="text-center py-12">
            <BookOpenIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Enter a search query and click "Discover Literature" to find relevant papers
            </p>
          </div>
        )}

        {/* Paper Detail Modal */}
        <PaperDetailModal
          paper={selectedPaper}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false)
            setSelectedPaper(null)
          }}
          onSaveToLibrary={documentId ? handleSaveSinglePaper : undefined}
          onInsertCitation={onCitationInsert ? handleInsertCitation : undefined}
          showSaveButton={!!documentId}
          showCitationButton={!!onCitationInsert}
        />
      </div>
    </div>
  )
}

